import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Inbox, Plus, Trash2 } from 'lucide-react';

import {
  useEdiSettings,
  useSaveEdiSettings,
  type EdiSettings,
} from '@/hooks/admin/useEdi';
import { cn } from '@/lib/utils';
import { CollapsibleCard } from '@/components/admin/CollapsibleCard';
import { useCustomerGroup } from '@/hooks/admin/useCustomerGroup';

/**
 * EDI Orders & Invoicing — per-customer settings card (Billing & comms tab).
 *
 * Chain customers email a PO to a dedicated inbound address instead of using
 * the portal; this card is the on/off switch + config: inbound address,
 * sender allowlist, who gets the order receipt/updates, the auto-submit
 * switch (review-first by default), store→location aliases, and free-text
 * extraction hints for the PO parser. Invoices route separately through the
 * Billing comms editor below (Accounting Email + the Invoices checkbox).
 */

const inputCls =
  'w-full rounded-lg border border-brix-border bg-brix-surface px-3 py-2 text-sm text-brix-ink placeholder:text-brix-muted/60 focus:outline-none focus:border-brix-primary/50';

interface AliasRow {
  alias: string;
  locationId: string;
}

function emptyDraft(): EdiSettings {
  return {
    enabled: false,
    inbound_address: '',
    sender_allowlist: [],
    order_notify_list: [],
    auto_submit: false,
    location_aliases: {},
    member_aliases: {},
    extraction_hints: null,
  };
}

export default function EdiSettingsCard({ customerId }: { customerId: string }) {
  // EDI is configured ONCE per chain, on the group master: one inbound
  // address, one item crosswalk, one auto-submit switch. The pipeline works
  // out which STORE each PO belongs to. So a member store gets a read-only
  // pointer rather than its own form — a second inbound address on a store
  // would be a config that can never receive anything.
  const { data: groupInfo } = useCustomerGroup(customerId);
  const memberOf = groupInfo?.member_of ?? null;
  const masterEdi = useEdiSettings(memberOf?.master_customer_id ?? '');

  const { data, isLoading } = useEdiSettings(customerId);
  const save = useSaveEdiSettings(customerId);

  const [draft, setDraft] = useState<EdiSettings>(emptyDraft());
  const [allowlistText, setAllowlistText] = useState('');
  const [notifyText, setNotifyText] = useState('');
  const [aliasRows, setAliasRows] = useState<AliasRow[]>([]);
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  // Seed the form whenever fresh settings land (and the user isn't mid-edit).
  // Chain gateways (members present) edit MEMBER aliases — PO store key →
  // store customer; single customers keep the location-alias editor.
  const members = data?.members ?? [];
  const isChain = members.length > 0;
  useEffect(() => {
    if (!data || dirty) return;
    const s = data.settings ?? emptyDraft();
    setDraft(s);
    setAllowlistText(s.sender_allowlist.join(', '));
    setNotifyText(s.order_notify_list.join(', '));
    const map = (data.members ?? []).length > 0 ? s.member_aliases : s.location_aliases;
    setAliasRows(Object.entries(map).map(([alias, locationId]) => ({ alias, locationId })));
  }, [data, dirty]);

  const locations = data?.locations ?? [];
  const configured = !!data?.settings;

  const touch = () => {
    setDirty(true);
    setMsg(null);
  };

  const splitList = (text: string) =>
    text.split(/[,\n;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);

  const canSave = useMemo(
    () => dirty && (!draft.enabled || draft.inbound_address.includes('@')),
    [dirty, draft],
  );

  const handleSave = () => {
    const aliasMap: Record<string, string> = {};
    for (const row of aliasRows) {
      if (row.alias.trim() && row.locationId) aliasMap[row.alias.trim()] = row.locationId;
    }
    save.mutate(
      {
        ...draft,
        sender_allowlist: splitList(allowlistText),
        order_notify_list: splitList(notifyText),
        location_aliases: isChain ? draft.location_aliases : aliasMap,
        member_aliases: isChain ? aliasMap : draft.member_aliases,
        extraction_hints: draft.extraction_hints?.trim() || null,
      },
      {
        onSuccess: () => {
          setDirty(false);
          setMsg({ kind: 'ok', text: 'EDI settings saved.' });
        },
        onError: (e) => setMsg({ kind: 'err', text: e instanceof Error ? e.message : 'save failed' }),
      },
    );
  };

  // A customer with EDI live gets the card open; everyone else gets one line.
  const liveEnabled = data?.settings?.enabled === true;

  // A member store has no EDI config of its own — the inbound address, item
  // crosswalk and auto-submit switch are set once on the master. Show where
  // they live rather than a second set of controls that wouldn't be read.
  if (memberOf) {
    const chainSettings = masterEdi.data?.settings ?? null;
    const on = !!chainSettings?.enabled;
    const groupWord = memberOf.group_kind === 'chain' ? 'chain' : 'franchise';
    return (
      <CollapsibleCard
        title="EDI Orders & Invoicing"
        icon={<Inbox size={14} className="text-brix-muted shrink-0" />}
        storageKey={`edi-${customerId}`}
        defaultOpen={false}
        summary={`🔒 managed on ${memberOf.master_name}${on ? ' · on' : ' · off'}`}
        desc={
          on ? (
            <>
              Purchase orders for this store arrive at{' '}
              <span className="font-mono text-brix-ink">{chainSettings?.inbound_address}</span> and
              are routed here by store code. The inbound address, item crosswalk and auto-submit
              switch are set once for the whole {groupWord}.
            </>
          ) : (
            <>
              EDI is not switched on for this {groupWord} yet. It is configured once on the master
              account, not per store.
            </>
          )
        }
      >
        <div className="flex items-center gap-3 flex-wrap">
          <Link
            to={`/admin/customers/${memberOf.master_customer_id}/billing`}
            className="text-xs font-semibold text-brix-primary hover:underline"
          >
            EDI settings on {memberOf.master_name} →
          </Link>
          {on ? (
            <Link
              to={`/admin/edi?customer=${memberOf.master_customer_id}`}
              className="text-xs font-semibold text-brix-primary hover:underline"
            >
              Review queue →
            </Link>
          ) : null}
        </div>
      </CollapsibleCard>
    );
  }

  return (
    <CollapsibleCard
      title="EDI Orders & Invoicing"
      icon={<Inbox size={14} className="text-brix-primary shrink-0" />}
      storageKey={`edi-${customerId}`}
      defaultOpen={liveEnabled}
      tone={liveEnabled ? 'active' : 'default'}
      summary={
        liveEnabled
          ? `On · ${data?.settings?.inbound_address ?? 'no address'}${data?.settings?.auto_submit ? ' · auto-submit' : ' · review first'}`
          : 'Off'
      }
      desc={
        <>
          Orders arrive as a PO emailed to the inbound address below, get parsed and
          matched automatically, and become a Service Fusion job — the sender gets the
          order receipt and delivery updates. Invoices route through the email slots in
          the Billing comms editor (check <em>Invoices</em> on the Accounting Email).
        </>
      }
      action={
        liveEnabled ? (
          <Link
            to={`/admin/edi?customer=${customerId}`}
            className="text-xs font-semibold text-brix-primary hover:underline"
          >
            Review queue →
          </Link>
        ) : undefined
      }
    >
      {isLoading ? (
        <div className="text-sm text-brix-muted">Loading…</div>
      ) : (
        <div className="space-y-4">
          {/* On/off + auto-submit */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <ToggleRow
              label="EDI ordering enabled"
              desc={configured ? 'Emails to the inbound address become orders' : 'Off — configure and enable to start'}
              enabled={draft.enabled}
              onChange={(v) => {
                setDraft((d) => ({ ...d, enabled: v }));
                touch();
              }}
            />
            <ToggleRow
              label="Auto-submit clean POs"
              desc="OFF = every PO waits in the review queue (recommended until this chain's POs prove reliable). ON = fully-matched POs create the SF job immediately; only problems hold."
              enabled={draft.auto_submit}
              onChange={(v) => {
                setDraft((d) => ({ ...d, auto_submit: v }));
                touch();
              }}
            />
          </div>

          {/* Addresses */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-semibold text-brix-muted">Inbound address</span>
              <input
                className={cn(inputCls, 'mt-1')}
                placeholder="themelt@alamedapointbg.com"
                value={draft.inbound_address}
                onChange={(e) => {
                  setDraft((d) => ({ ...d, inbound_address: e.target.value }));
                  touch();
                }}
              />
              <span className="text-[11px] text-brix-muted">
                Must be on alamedapointbg.com — any address on that domain receives mail.
              </span>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-brix-muted">Allowed senders</span>
              <input
                className={cn(inputCls, 'mt-1')}
                placeholder="themelt.com, po@themelt.com"
                value={allowlistText}
                onChange={(e) => {
                  setAllowlistText(e.target.value);
                  touch();
                }}
              />
              <span className="text-[11px] text-brix-muted">
                Domains or full addresses, comma-separated. Empty = accept any sender.
              </span>
            </label>
          </div>

          <label className="block">
            <span className="text-xs font-semibold text-brix-muted">
              Order notifications (receipt + delivery updates)
            </span>
            <input
              className={cn(inputCls, 'mt-1')}
              placeholder="ops@themelt.com"
              value={notifyText}
              onChange={(e) => {
                setNotifyText(e.target.value);
                touch();
              }}
            />
            <span className="text-[11px] text-brix-muted">
              The person who emailed the PO always gets these; addresses here are copied too.
            </span>
          </label>

          {/* Store aliases */}
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-brix-muted">
                {isChain
                  ? 'Store aliases (PO store name/number → store customer)'
                  : 'Store aliases (PO store name/number → location)'}
              </span>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs font-semibold text-brix-primary hover:underline"
                onClick={() => {
                  setAliasRows((r) => [...r, { alias: '', locationId: '' }]);
                  touch();
                }}
              >
                <Plus size={12} /> Add alias
              </button>
            </div>
            {aliasRows.length === 0 ? (
              <p className="text-[11px] text-brix-muted mt-1">
                None yet — the matcher tries the store number (#NN) and name automatically;
                add aliases here when a store keeps landing in review.
              </p>
            ) : (
              <div className="mt-2 space-y-2">
                {aliasRows.map((row, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      className={cn(inputCls, 'flex-1')}
                      placeholder="davis / #28 / store name on the PO"
                      value={row.alias}
                      onChange={(e) => {
                        setAliasRows((rows) => rows.map((r, j) => (j === i ? { ...r, alias: e.target.value } : r)));
                        touch();
                      }}
                    />
                    <select
                      className={cn(inputCls, 'flex-1')}
                      value={row.locationId}
                      onChange={(e) => {
                        setAliasRows((rows) => rows.map((r, j) => (j === i ? { ...r, locationId: e.target.value } : r)));
                        touch();
                      }}
                    >
                      <option value="">{isChain ? '— pick a store customer —' : '— pick a location —'}</option>
                      {isChain
                        ? members.map((m) => (
                            <option key={m.customer_id} value={m.customer_id}>
                              {m.name}
                              {m.city ? ` · ${m.city}` : ''}
                            </option>
                          ))
                        : locations.map((l) => (
                            <option key={l.id} value={l.id}>
                              {l.display_name ?? l.id.slice(0, 8)}
                              {l.location_code ? ` (${l.location_code})` : ''}
                            </option>
                          ))}
                    </select>
                    <button
                      type="button"
                      className="text-brix-muted hover:text-red-600"
                      title="Remove alias"
                      onClick={() => {
                        setAliasRows((rows) => rows.filter((_, j) => j !== i));
                        touch();
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Extraction hints */}
          <label className="block">
            <span className="text-xs font-semibold text-brix-muted">
              PO extraction hints (optional)
            </span>
            <textarea
              className={cn(inputCls, 'mt-1 min-h-[60px]')}
              placeholder='e.g. "Store number appears top-right as LOC-NNN. Item codes are in the SKU column."'
              value={draft.extraction_hints ?? ''}
              onChange={(e) => {
                setDraft((d) => ({ ...d, extraction_hints: e.target.value }));
                touch();
              }}
            />
          </label>

          {/* Save row */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={!canSave || save.isPending}
              onClick={handleSave}
              className={cn(
                'rounded-lg px-4 py-2 text-sm font-semibold',
                canSave && !save.isPending
                  ? 'bg-brix-primary text-white hover:opacity-90'
                  : 'bg-brix-surface-alt text-brix-muted cursor-not-allowed',
              )}
            >
              {save.isPending ? 'Saving…' : 'Save EDI settings'}
            </button>
            {msg && (
              <span className={cn('text-xs', msg.kind === 'ok' ? 'text-emerald-600' : 'text-red-600')}>
                {msg.text}
              </span>
            )}
          </div>
        </div>
      )}
    </CollapsibleCard>
  );
}

function ToggleRow({
  label,
  desc,
  enabled,
  onChange,
}: {
  label: string;
  desc: string;
  enabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      className={cn(
        'text-left rounded-lg border px-3 py-2.5 transition-colors',
        enabled ? 'border-brix-primary/40 bg-brix-primary-tint/40' : 'border-brix-border bg-brix-surface',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-brix-ink">{label}</span>
        <span
          className={cn(
            'inline-flex h-5 w-9 items-center rounded-full px-0.5 transition-colors',
            enabled ? 'bg-brix-primary' : 'bg-brix-border',
          )}
        >
          <span
            className={cn(
              'h-4 w-4 rounded-full bg-white transition-transform',
              enabled ? 'translate-x-4' : 'translate-x-0',
            )}
          />
        </span>
      </div>
      <p className="text-[11px] text-brix-muted mt-1 leading-relaxed">{desc}</p>
    </button>
  );
}
